/**
 * Leviathan Web - System Settings Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Tenant-level system configuration and preferences
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Settings,
  Building2,
  Hash,
  ToggleLeft,
  ClipboardList,
  Save,
  Check,
  X,
  RefreshCw,
} from 'lucide-react';
import { API_URL } from '@/config/api';

// Types
interface SystemSettingsData {
  id: string;
  tenantId: string;
  companyName: string;
  companyLogo: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  federalTaxId: string | null;
  stateTaxId: string | null;
  fiscalYearStart: number;
  timezone: string;
  dateFormat: string;
  currency: string;
  invoicePrefix: string;
  invoiceNextNum: number;
  orderPrefix: string;
  orderNextNum: number;
  customerPrefix: string;
  customerNextNum: number;
  bolPrefix: string;
  bolNextNum: number;
  defaultPaymentTerms: number;
  defaultCreditLimit: number | null;
  enableTaxExemptions: boolean;
  enableCreditHolds: boolean;
  enableDeliveryTracking: boolean;
  auditRetentionDays: number;
  auditLoginEvents: boolean;
  auditViewEvents: boolean;
  createdAt: string;
  updatedAt: string;
}

type TabType = 'company' | 'business' | 'numbering' | 'features' | 'audit';

/**
 * Form Field Component
 */
function FormField({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all disabled:opacity-50"
      />
    </div>
  );
}

/**
 * Toggle Switch Component
 */
function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 bg-background border border-panel-border">
      <div>
        <div className="text-text-primary font-mono text-sm">{label}</div>
        {description && (
          <div className="text-text-tertiary font-mono text-xs mt-1">{description}</div>
        )}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-all ${
          checked ? 'bg-amber' : 'bg-panel-border'
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
            checked ? 'left-7' : 'left-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Number Sequence Card Component
 */
function NumberSequenceCard({
  label,
  prefix,
  nextNum,
  onPrefixChange,
  onNextNumChange,
}: {
  label: string;
  prefix: string;
  nextNum: number;
  onPrefixChange: (value: string) => void;
  onNextNumChange: (value: number) => void;
}) {
  const preview = `${prefix}-${String(nextNum).padStart(6, '0')}`;

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-3">
          {label}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-text-tertiary text-xs font-mono mb-1">Prefix</label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => onPrefixChange(e.target.value.toUpperCase())}
              maxLength={10}
              className="w-full bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono text-sm"
            />
          </div>
          <div>
            <label className="block text-text-tertiary text-xs font-mono mb-1">Next #</label>
            <input
              type="number"
              value={nextNum}
              onChange={(e) => onNextNumChange(parseInt(e.target.value) || 1)}
              min={1}
              className="w-full bg-background border border-panel-border focus:border-amber px-3 py-2 text-text-primary font-mono text-sm"
            />
          </div>
        </div>

        <div className="text-center">
          <span className="text-text-tertiary font-mono text-xs">Preview: </span>
          <span
            className="text-amber font-mono text-sm font-bold"
            style={{ textShadow: '0 0 10px rgba(255, 149, 0, 0.6)' }}
          >
            {preview}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * System Settings Page Component
 */
const SystemSettings = () => {
  const { accessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('company');
  const [settings, setSettings] = useState<SystemSettingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-settings`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        // Initialize if not found
        const initResponse = await fetch(`${API_URL}/api/system-settings/initialize`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (initResponse.ok) {
          const data = await initResponse.json();
          setSettings(data.settings || data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setIsSaving(true);
    setMessage(null);

    try {
      const response = await fetch(`${API_URL}/api/system-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        setMessage({ type: 'success', text: 'Settings saved successfully' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof SystemSettingsData, value: unknown) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'company', label: 'Company', icon: <Building2 className="w-4 h-4" /> },
    { id: 'business', label: 'Business', icon: <Settings className="w-4 h-4" /> },
    { id: 'numbering', label: 'Numbering', icon: <Hash className="w-4 h-4" /> },
    { id: 'features', label: 'Features', icon: <ToggleLeft className="w-4 h-4" /> },
    { id: 'audit', label: 'Audit', icon: <ClipboardList className="w-4 h-4" /> },
  ];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'Pacific/Honolulu',
    'America/Anchorage',
  ];

  const dateFormats = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
  const currencies = ['USD', 'CAD', 'EUR', 'GBP', 'MXN'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <h1
            className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
            style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
          >
            System Settings
          </h1>
          <p className="text-text-secondary text-sm font-mono mt-1">
            Configure system-wide preferences and defaults
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 border flex items-center justify-between ${
            message.type === 'success'
              ? 'bg-success-green/10 border-success-green/50 text-success-green'
              : 'bg-error-red/10 border-error-red/50 text-error-red'
          } font-mono text-sm`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <Check className="w-4 h-4" />
            ) : (
              <X className="w-4 h-4" />
            )}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 border-b border-panel-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-6 py-3 font-mono text-sm uppercase tracking-wider transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-amber border-b-2 border-amber bg-amber/5'
                : 'text-text-secondary hover:text-text-primary hover:bg-panel-border/20'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="relative stat-card">
        <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
        <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

        <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-amber animate-spin mx-auto mb-4" />
              <div className="text-text-secondary font-mono">Loading settings...</div>
            </div>
          ) : settings ? (
            <>
              {/* Company Tab */}
              {activeTab === 'company' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Company Information
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <FormField
                        label="Company Name"
                        value={settings.companyName || ''}
                        onChange={(v) => updateField('companyName', v)}
                      />
                    </div>
                    <FormField
                      label="Phone"
                      value={settings.phone || ''}
                      onChange={(v) => updateField('phone', v)}
                    />
                    <FormField
                      label="Email"
                      value={settings.email || ''}
                      onChange={(v) => updateField('email', v)}
                      type="email"
                    />
                    <FormField
                      label="Website"
                      value={settings.website || ''}
                      onChange={(v) => updateField('website', v)}
                    />
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mt-8 mb-4">
                    Address
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <FormField
                        label="Street Address"
                        value={settings.address || ''}
                        onChange={(v) => updateField('address', v)}
                      />
                    </div>
                    <FormField
                      label="City"
                      value={settings.city || ''}
                      onChange={(v) => updateField('city', v)}
                    />
                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        label="State"
                        value={settings.state || ''}
                        onChange={(v) => updateField('state', v)}
                      />
                      <FormField
                        label="ZIP"
                        value={settings.zip || ''}
                        onChange={(v) => updateField('zip', v)}
                      />
                      <FormField
                        label="Country"
                        value={settings.country || 'USA'}
                        onChange={(v) => updateField('country', v)}
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mt-8 mb-4">
                    Tax Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Federal Tax ID (EIN)"
                      value={settings.federalTaxId || ''}
                      onChange={(v) => updateField('federalTaxId', v)}
                      placeholder="XX-XXXXXXX"
                    />
                    <FormField
                      label="State Tax ID"
                      value={settings.stateTaxId || ''}
                      onChange={(v) => updateField('stateTaxId', v)}
                    />
                  </div>
                </div>
              )}

              {/* Business Tab */}
              {activeTab === 'business' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Business Settings
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                        Fiscal Year Start
                      </label>
                      <select
                        value={settings.fiscalYearStart}
                        onChange={(e) => updateField('fiscalYearStart', parseInt(e.target.value))}
                        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                      >
                        {months.map((month, index) => (
                          <option key={month} value={index + 1}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                        Timezone
                      </label>
                      <select
                        value={settings.timezone}
                        onChange={(e) => updateField('timezone', e.target.value)}
                        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                      >
                        {timezones.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                        Date Format
                      </label>
                      <select
                        value={settings.dateFormat}
                        onChange={(e) => updateField('dateFormat', e.target.value)}
                        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                      >
                        {dateFormats.map((format) => (
                          <option key={format} value={format}>
                            {format}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                        Currency
                      </label>
                      <select
                        value={settings.currency}
                        onChange={(e) => updateField('currency', e.target.value)}
                        className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
                      >
                        {currencies.map((currency) => (
                          <option key={currency} value={currency}>
                            {currency}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mt-8 mb-4">
                    Default Terms
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="Default Payment Terms (Days)"
                      value={settings.defaultPaymentTerms?.toString() || '30'}
                      onChange={(v) => updateField('defaultPaymentTerms', parseInt(v) || 30)}
                      type="number"
                    />
                    <FormField
                      label="Default Credit Limit ($)"
                      value={settings.defaultCreditLimit?.toString() || ''}
                      onChange={(v) => updateField('defaultCreditLimit', parseFloat(v) || null)}
                      type="number"
                      placeholder="No limit"
                    />
                  </div>
                </div>
              )}

              {/* Numbering Tab */}
              {activeTab === 'numbering' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Document Numbering
                  </h2>
                  <p className="text-text-secondary font-mono text-sm mb-6">
                    Configure prefixes and starting numbers for different document types.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <NumberSequenceCard
                      label="Invoice Numbers"
                      prefix={settings.invoicePrefix}
                      nextNum={settings.invoiceNextNum}
                      onPrefixChange={(v) => updateField('invoicePrefix', v)}
                      onNextNumChange={(v) => updateField('invoiceNextNum', v)}
                    />
                    <NumberSequenceCard
                      label="Order Numbers"
                      prefix={settings.orderPrefix}
                      nextNum={settings.orderNextNum}
                      onPrefixChange={(v) => updateField('orderPrefix', v)}
                      onNextNumChange={(v) => updateField('orderNextNum', v)}
                    />
                    <NumberSequenceCard
                      label="Customer Numbers"
                      prefix={settings.customerPrefix}
                      nextNum={settings.customerNextNum}
                      onPrefixChange={(v) => updateField('customerPrefix', v)}
                      onNextNumChange={(v) => updateField('customerNextNum', v)}
                    />
                    <NumberSequenceCard
                      label="BOL Numbers"
                      prefix={settings.bolPrefix}
                      nextNum={settings.bolNextNum}
                      onPrefixChange={(v) => updateField('bolPrefix', v)}
                      onNextNumChange={(v) => updateField('bolNextNum', v)}
                    />
                  </div>
                </div>
              )}

              {/* Features Tab */}
              {activeTab === 'features' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Feature Toggles
                  </h2>
                  <p className="text-text-secondary font-mono text-sm mb-6">
                    Enable or disable system-wide features.
                  </p>

                  <div className="space-y-3">
                    <ToggleSwitch
                      label="Tax Exemptions"
                      description="Allow customers to be marked as tax exempt"
                      checked={settings.enableTaxExemptions}
                      onChange={(v) => updateField('enableTaxExemptions', v)}
                    />
                    <ToggleSwitch
                      label="Credit Holds"
                      description="Enable credit hold functionality for customers"
                      checked={settings.enableCreditHolds}
                      onChange={(v) => updateField('enableCreditHolds', v)}
                    />
                    <ToggleSwitch
                      label="Delivery Tracking"
                      description="Enable delivery tracking for orders"
                      checked={settings.enableDeliveryTracking}
                      onChange={(v) => updateField('enableDeliveryTracking', v)}
                    />
                  </div>
                </div>
              )}

              {/* Audit Tab */}
              {activeTab === 'audit' && (
                <div className="space-y-6">
                  <h2 className="text-xl font-display text-text-primary uppercase tracking-wide mb-6">
                    Audit Settings
                  </h2>
                  <p className="text-text-secondary font-mono text-sm mb-6">
                    Configure audit logging preferences and retention.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <FormField
                      label="Audit Log Retention (Days)"
                      value={settings.auditRetentionDays?.toString() || '365'}
                      onChange={(v) => updateField('auditRetentionDays', parseInt(v) || 365)}
                      type="number"
                    />
                  </div>

                  <h3 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4">
                    Event Logging
                  </h3>

                  <div className="space-y-3">
                    <ToggleSwitch
                      label="Log Login Events"
                      description="Record user login and logout events"
                      checked={settings.auditLoginEvents}
                      onChange={(v) => updateField('auditLoginEvents', v)}
                    />
                    <ToggleSwitch
                      label="Log View Events"
                      description="Record when users view records (high volume)"
                      checked={settings.auditViewEvents}
                      onChange={(v) => updateField('auditViewEvents', v)}
                    />
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end mt-8 pt-6 border-t border-panel-border">
                <button
                  onClick={saveSettings}
                  disabled={isSaving}
                  className="flex items-center space-x-2 bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
                  style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
                >
                  {isSaving ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-error-red font-mono">Failed to load settings</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
